import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const LegalEducation = () => {
  const [activeTab, setActiveTab] = useState('ownership');

  const educationContent = {
    ownership: {
      title: "Propriété Étrangère",
      icon: "Globe",
      content: [
        {
          question: "Les étrangers peuvent-ils acheter des biens immobiliers en Côte d'Ivoire ?",
          answer: `Oui, les étrangers peuvent acquérir des biens immobiliers en Côte d'Ivoire, mais avec certaines restrictions. Les non-résidents peuvent acheter des appartements, des maisons et des terrains à usage commercial, mais l'acquisition de terrains agricoles est généralement réservée aux citoyens ivoiriens.\n\nLes investisseurs étrangers doivent obtenir un certificat de non-objection du Ministère de l'Intérieur et respecter les procédures d'enregistrement spécifiques.`
        },
        {
          question: "Quelles sont les procédures spécifiques pour les investisseurs de la diaspora ?",
          answer: `Les Ivoiriens de la diaspora bénéficient de procédures simplifiées. Ils peuvent acquérir des biens par procuration notariée, avec des facilités pour l'ouverture de comptes bancaires locaux.\n\nUn représentant légal peut être désigné pour faciliter les démarches administratives et le suivi des transactions.`
        }
      ]
    },
    inheritance: {
      title: "Lois Successorales",
      icon: "Users",
      content: [
        {
          question: "Comment fonctionne l'héritage immobilier en Côte d'Ivoire ?",
          answer: `L'héritage immobilier suit le droit civil français adapté au contexte ivoirien. En l'absence de testament, la succession suit l'ordre légal : conjoint survivant, enfants, puis famille élargie.\n\nLes biens immobiliers peuvent être légués par testament, mais certaines parts réservataires doivent être respectées pour les héritiers directs.`
        },
        {
          question: "Quels sont les droits du conjoint survivant ?",
          answer: `Le conjoint survivant a droit à une part de l'héritage qui varie selon la présence d'enfants. En présence d'enfants, le conjoint hérite d'un quart en pleine propriété.\n\nSans enfants mais avec des parents survivants, le conjoint hérite de la moitié. Sans descendants ni ascendants, le conjoint hérite de la totalité.`
        }
      ]
    },
    taxation: {
      title: "Fiscalité Immobilière",
      icon: "Calculator",
      content: [
        {
          question: "Quels sont les impôts liés à l'achat d'un bien immobilier ?",
          answer: `L'acquisition d'un bien immobilier est soumise à plusieurs taxes :\n- Droits d'enregistrement : 5% de la valeur du bien\n- Taxe de publicité foncière : 1% de la valeur\n- Frais de notaire : environ 2-3% du prix de vente\n\nCes frais s'ajoutent au prix d'achat et doivent être budgétisés lors de l'acquisition.`
        },
        {
          question: "Comment est calculée la taxe foncière annuelle ?",
          answer: `La taxe foncière est calculée sur la valeur locative cadastrale du bien, avec un taux qui varie selon la commune. À Abidjan, le taux moyen est de 15% de la valeur locative.\n\nLes propriétés neuves bénéficient souvent d'exonérations temporaires de 2 à 5 ans selon la zone d'implantation.`
        }
      ]
    }
  };

  const tabs = [
    { key: 'ownership', label: 'Propriété Étrangère', icon: 'Globe' },
    { key: 'inheritance', label: 'Lois Successorales', icon: 'Users' },
    { key: 'taxation', label: 'Fiscalité', icon: 'Calculator' }
  ];

  return (
    <section className="py-24 bg-transparent border-none outline-none relative z-10">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-heading font-bold text-white mb-6 tracking-tight">
            Éducation Juridique
          </h2>
          <p className="text-lg text-white/50 max-w-2xl mx-auto font-light leading-relaxed">
            Comprenez les aspects légaux de l'immobilier ivoirien avec nos guides détaillés.
          </p>
        </div>

        {/* Conteneur principal sans bordure */}
        <div className="bg-[#111111] rounded-3xl shadow-2xl border-none outline-none overflow-hidden">
          
          {/* Tab Navigation (Style iOS/Pills) */}
          <div className="p-6 md:p-8 bg-white/[0.02]">
            <div className="flex flex-wrap gap-3 justify-center md:justify-start">
              {tabs?.map((tab) => (
                <button
                  key={tab?.key}
                  onClick={() => setActiveTab(tab?.key)}
                  className={`flex items-center gap-2.5 px-6 py-3 rounded-full text-sm font-bold transition-all duration-300 border-none outline-none ${
                    activeTab === tab?.key
                      ? 'bg-brand-gold text-black shadow-lg shadow-brand-gold/20' 
                      : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Icon 
                    name={tab?.icon} 
                    size={18} 
                    className={activeTab === tab?.key ? 'text-black' : 'text-current'} 
                  />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6 md:p-10">
            <div className="mb-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-brand-gold/10 flex items-center justify-center flex-shrink-0 shadow-inner">
                  <Icon 
                    name={educationContent?.[activeTab]?.icon} 
                    size={24} 
                    className="text-brand-gold" 
                  />
                </div>
                <h3 className="text-3xl font-heading font-bold text-white tracking-tight">
                  {educationContent?.[activeTab]?.title}
                </h3>
              </div>
            </div>

            <div className="space-y-6">
              {educationContent?.[activeTab]?.content?.map((item, index) => (
                <div key={index} className="bg-white/5 rounded-2xl p-6 md:p-8 border-none outline-none transition-colors hover:bg-white/[0.07]">
                  <h4 className="text-lg font-heading font-bold text-white mb-4 flex items-start gap-3">
                    <Icon name="HelpCircle" size={20} className="text-brand-gold flex-shrink-0 mt-1" />
                    {item?.question}
                  </h4>
                  <div className="ml-8">
                    {item?.answer?.split('\n\n')?.map((paragraph, pIndex) => (
                      <p key={pIndex} className="text-white/60 font-light leading-relaxed mb-4 last:mb-0">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Additional Resources (Bento Style) */}
            <div className="mt-12 p-8 bg-gradient-to-br from-blue-900/20 to-blue-500/5 rounded-3xl relative overflow-hidden group">
              <div className="absolute inset-0 bg-blue-500/10 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
              
              <div className="relative z-10">
                <h4 className="text-xl font-heading font-bold text-white mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                    <Icon name="BookOpen" size={20} className="text-blue-400" />
                  </div>
                  Ressources Complémentaires
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-4 p-4 bg-white/5 rounded-2xl border-none hover:bg-white/10 transition-colors cursor-pointer">
                    <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0">
                      <Icon name="FileText" size={18} className="text-white/70" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white">Code Civil Ivoirien</p>
                      <p className="text-xs text-white/40 font-light">Textes de référence</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 p-4 bg-white/5 rounded-2xl border-none hover:bg-white/10 transition-colors cursor-pointer">
                    <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0">
                      <Icon name="Scale" size={18} className="text-white/70" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white">Jurisprudence</p>
                      <p className="text-xs text-white/40 font-light">Décisions de justice</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default LegalEducation;